/**
 * App-wide branding (issue #20): one settings singleton, not per-tournament.
 * Lets a tournament director replace the app's own name, tagline, and logo
 * with their own organization's, everywhere the nav and footer render — the
 * logo and name lock up in the public home page banner, and the logo becomes
 * the browser tab icon.
 *
 * The logo is uploaded, not linked: an organization has a PNG of its mark, not
 * a CDN to host it on. Pasting a URL is still supported, under the picker.
 */
import { useEffect, useState } from "react";
import { MAX_LOGO_BYTES } from "@alpha/shared";
import { api, apiErrorMessage } from "../../lib/api";
import { useBranding, useSiteSettings } from "../../lib/siteSettings";
import { useSiteSettingsMutation } from "../../lib/adminData";
import { ImageUploadField } from "../../components/ImageUploadField";
import { BrandColorField } from "../../components/BrandColorField";
import { LOGO_UPLOAD_ENDPOINT } from "../../lib/imageUpload";
import { Button, Field, Input, PageHeader, SectionCard } from "../../components/ui";

export function AdminBranding() {
  const settingsQ = useSiteSettings();
  const current = useBranding();

  const [orgName, setOrgName] = useState(current.orgName);
  const [tagline, setTagline] = useState(current.tagline);
  const [logoUrl, setLogoUrl] = useState(current.logoUrl ?? "");
  const [brandColor, setBrandColor] = useState(current.brandColor ?? null);
  const [error, setError] = useState<string>();

  // Seed the form once the real settings arrive — the fields start on the
  // fallback defaults, which flash briefly if a customization is already set.
  useEffect(() => {
    if (!settingsQ.data) return;
    setOrgName(settingsQ.data.orgName);
    setTagline(settingsQ.data.tagline);
    setLogoUrl(settingsQ.data.logoUrl ?? "");
    setBrandColor(settingsQ.data.brandColor ?? null);
  }, [settingsQ.data]);

  const save = useSiteSettingsMutation(() =>
    api.put("/admin/settings", {
      orgName: orgName.trim(),
      tagline: tagline.trim(),
      logoUrl: logoUrl.trim() || null,
      brandColor,
    }),
  );

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Branding"
        subtitle="Replace the app's own name, tagline, and logo with your organization's."
      />

      <SectionCard
        title="Site identity"
        description="Shown in the nav and footer on every page, in the home page banner, and in the browser tab."
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!orgName.trim()) return;
            save.mutate(undefined, {
              onSuccess: () => setError(undefined),
              onError: (err) => setError(apiErrorMessage(err, "Could not save branding.")),
            });
          }}
        >
          <Field
            label="Organization name"
            hint="Replaces &ldquo;Alpha Tournaments&rdquo; in the nav and footer, and headlines the home page banner."
          >
            <Input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              maxLength={80}
              placeholder="River City Baseball"
              required
            />
          </Field>
          <Field
            label="Tagline"
            hint="Shown in the footer and under the home page banner headline."
          >
            <Input
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              maxLength={160}
              placeholder="Youth baseball, run beautifully."
            />
          </Field>
          <ImageUploadField
            label="Logo"
            hint="Replaces the diamond mark in the nav, the footer, the home page banner, and the browser tab. Square images work best in the tab."
            endpoint={LOGO_UPLOAD_ENDPOINT}
            maxBytes={MAX_LOGO_BYTES}
            value={logoUrl}
            onChange={setLogoUrl}
            shape="square"
            saveHint="Save branding to apply it."
            linkPlaceholder="https://example.com/logo.png"
          />
          <BrandColorField value={brandColor} onChange={setBrandColor} />

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <Button type="submit" disabled={!orgName.trim() || save.isPending}>
            {save.isPending ? "Saving…" : "Save branding"}
          </Button>
        </form>
      </SectionCard>
    </div>
  );
}
